// The operator token: one sessionStorage key, read and written only here.
//
// SEC-09: the token lives in sessionStorage, so it ends with the tab and is not
// shared across tabs or kept on disk for later visits. A token an earlier build
// left in localStorage is moved over once and deleted from localStorage.
//
// ?demo=1 is an audit and preview switch: when no token is stored it signs
// in with the fixture DEMO_TOKEN for this page load only (never persisted).
// App.tsx pairs it with the fixture api, and only then, so the demo never
// touches the live API and no token is ever written to storage.
import { DEMO_TOKEN } from "./fixtures";

export const TOKEN_KEY = "dashboardToken";

function session(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    // Storage blocked (privacy mode, sandboxed frame): the token lives for
    // this page load only.
    return null;
  }
}

function legacy(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** One-time move of a token from localStorage (older builds) to sessionStorage. */
export function migrateToken(): void {
  const old = legacy();
  try {
    const stored = old?.getItem(TOKEN_KEY) ?? null;
    if (!old || stored === null) return;
    const now = session();
    if (now && !now.getItem(TOKEN_KEY)) now.setItem(TOKEN_KEY, stored);
    old.removeItem(TOKEN_KEY);
  } catch {
    // Blocked storage: nothing to move.
  }
}

function demoRequested(): boolean {
  try {
    return typeof location !== "undefined" && new URLSearchParams(location.search).get("demo") === "1";
  } catch {
    return false;
  }
}

export function readToken(): string | null {
  migrateToken();
  const stored = session()?.getItem(TOKEN_KEY) ?? null;
  if (stored) return stored;
  return demoRequested() ? DEMO_TOKEN : null;
}

export function saveToken(token: string): void {
  session()?.setItem(TOKEN_KEY, token);
  // Never leave a copy behind in the long-lived store.
  legacy()?.removeItem(TOKEN_KEY);
}

export function clearToken(): void {
  session()?.removeItem(TOKEN_KEY);
  legacy()?.removeItem(TOKEN_KEY);
}
